<template>
  <v-form ref="form" @submit.prevent="onSubmit">
    <v-sheet class="border-opacity-25 overflow-hidden border-thin rounded-lg pa-5">
      <v-row v-if="isCreate">
        <v-col cols="12" md="12">
          <v-text-field
            v-model="team.name"
            label="Team Name"
            :rules="[rules.required, rules.teamUnique(team.name, team.id)]"></v-text-field>
        </v-col>
      </v-row>
      <v-divider v-if="isCreate"></v-divider>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="team.website"
            label="Website"
            placeholder="https://www.72fest.com"
            hint="Full URL"
            :rules="[rules.url]"></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="team.instagram"
            label="Instagram"
            placeholder="72filmfest"
            hint="Instagram Username"
            :rules="[rules.instagram]"></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="team.facebook"
            label="Facebook"
            placeholder="72filmfest"
            hint="Facebook Username"
            :rules="[rules.facebook]"></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="team.twitter"
            label="X"
            placeholder="@72filmfest"
            hint="X Username starting with @"
            :rules="[rules.twitter]"></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="team.youtube"
            label="YouTube Channel"
            placeholder="72filmfest"
            hint="YouTube Channel Name"
            :rules="[rules.youtube]"></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="team.vimeo"
            label="Vimeo"
            placeholder="72filmfest"
            hint="Vimeo Username"
            :rules="[rules.vimeo]"></v-text-field>
        </v-col>
      </v-row>
      <v-divider></v-divider>
      <v-row>
        <v-col cols="12" md="12"
          >Bio
          <Editor
            v-model="team.bio"
            :init="{
              base_url: '/tinymce',
              suffix: '.min',
              plugins: 'lists link image table code',
              toolbar:
                'undo redo | blocks | bold italic | alignleft aligncenter alignright | bullist numlist | link image table | code',
              skin: 'oxide',
              content_css: 'default',
            }"
            licenseKey="gpl"></Editor>
        </v-col>
      </v-row>
      <v-row>
        <v-col v-if="allowClose">
          <v-btn
            color="secondary"
            block
            class="rounded-lg mb-8"
            elevation="2"
            @click="emit('teamEditCanceled')">
            Cancel
          </v-btn>
        </v-col>
        <v-col>
          <v-btn
            :loading="isSubmitting"
            color="primary"
            block
            class="rounded-lg mb-8"
            elevation="2"
            @click="onSubmit">
            {{ saveLabel }}
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>
  </v-form>
</template>
<script setup lang="ts">
import Editor from '@tinymce/tinymce-vue';
import { rules } from '@/helpers';
import { computed, ref, toRef, watch } from 'vue';
import type { VForm } from 'vuetify/components';
import { ITeamDTO, TeamDTO } from '@/dtos';
import { useTeamStore } from '@/stores';
import { useUpdateStatusStore } from '@/stores/modules';
import { SNACKSTATUS } from '@/stores/util';

interface Props {
  team: ITeamDTO;
  isCreate: boolean;
  allowClose: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits(['teamEditComplete', 'teamEditCanceled', 'snack']);
const team = ref<ITeamDTO>(new TeamDTO());
watch(
  () => props.team,
  () => {
    team.value = new TeamDTO(props.team);
  },
  { immediate: true, deep: true },
);

const isCreate = toRef(props, 'isCreate', false);
const allowClose = toRef(props, 'allowClose', false);
import '@/components/tinymce/tinymce';
const form = ref<InstanceType<typeof VForm> | null>();
const isSubmitting = ref(false);
const teamStore = useTeamStore();
const statusStore = useUpdateStatusStore();

const saveLabel = computed(() => {
  if (isCreate.value == true) {
    return 'Create Team';
  } else {
    return 'Save';
  }
});

const status = computed(() => statusStore.getStatus(`team-${team.value.id}`));
watch(status, (value) => {
  if (!value) return;
  emit('snack', 'Team', value, statusStore.getError(`team-${team.value.id}`));
  emit('teamEditComplete');
  isSubmitting.value = false;
});

watch(
  () => statusStore.getStatus(`team-create`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `team-create`;
    emit('snack', 'Team', value, statusStore.getError(key));
    emit('teamEditComplete');
    isSubmitting.value = false;
  },
);

const onSubmit = async () => {
  if (!form.value) {
    return;
  }
  const valid = await form.value.validate();
  if (valid.valid) {
    isSubmitting.value = true;
    if (isCreate.value) {
      teamStore.create.mutateAsync(team.value);
    } else {
      teamStore.update.mutateAsync(team.value);
    }
  }
};
</script>
